import { useForm } from "react-hook-form";
import TextInput from "../../components/inputs/text";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema, schemaDefaultValues } from "../contratacao/register-contracting/form-credit-card/schema";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faCreditCard, faUser } from "@fortawesome/free-regular-svg-icons";
import { faLock, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import MaskInput from "../../components/inputs/mask";
import { BtnSubmit } from "../../components/button/button-submit/button-submit";

import libForm from "../../util/libForm";
import { useEffect, useRef, useState } from "react";
import FlagsCard from "../contratacao/register-contracting/form-credit-card/flags";
import { BRANDS, formatCardNumberSpaces } from "../../util/libCard";
import reqCardToken from "../../services/reqAsaas/reqCardToken";
import functions from "../../util/functions";
import { toast } from "react-toastify";
import LoadingFull from "../../components/loading/loading-full";

const FormCreditCard = ({ onCloseModal }) => {
	const [cardNumber, setCardNumber] = useState("");
	const [brand, setBrand] = useState(null);
	const [cvvLen, setCvvLen] = useState(3);
	const [clientSideError, setClientSideError] = useState("");
	const numberInputRef = useRef(null);

	const {
		register,
		handleSubmit,
		setValue,
		setFocus,
		setError,
		reset,
		watch,
		control,
		formState: { errors, isSubmitting },
	} = useForm({
		mode: "all",
		resolver: zodResolver(schema),
		defaultValues: schemaDefaultValues,
	});

	const onLocalSubmit = async (data) => {
		try {
			const remote_ip = await functions.getClientIP();

			const [expiryMonth, expiryYear] = data.expirationDate.split("/");

			const card = {
				...data,
				ambiente: 1,
				remote_ip,
				expiryMonth,
				expiryYear,
			};

			await reqCardToken.insert(card);

			toast.success(`Cartão cadastrado com sucesso!`);
			onCloseModal();
		} catch (error) {
			toast.error("Erro ao cadastrar o cartão. Tente novamente. " + error.message);
		}
	};

	// Detecta bandeira + tamanho do CVV
	useEffect(() => {
		const clean = cardNumber.replace(/\s+/g, "");
		const found = BRANDS.find((b) => b.test(clean));
		setBrand(found || null);
		setCvvLen(found?.id === "amex" ? 4 : 3); // fallback; (se quiser incluir Amex futuramente)
	}, [cardNumber]);

	return (
		<form className="flex w-full " onSubmit={handleSubmit(onLocalSubmit)} aria-labelledby="checkout-title">
			{/* Coluna principal */}
			<section className="space-y-4">
				{/* Card de segurança + bandeiras */}
				<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div className="flex items-center gap-2 text-emerald-700">
							<FontAwesomeIcon icon={faShieldHalved} className="h-5 w-5" />
							<span className="text-sm font-semibold">Ambiente protegido por SSL</span>
						</div>
						<FlagsCard />
					</div>
				</div>

				{/* Card dos dados do cartão */}
				<div
					className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm
                grid grid-cols-1 sm:grid-cols-2 gap-4"
				>
					<header className="mb-2 sm:col-span-2">
						<h2 className="text-xl font-bold tracking-tight">Dados do cartão</h2>
						<p className="text-sm text-gray-500 mt-1">
							Não salvamos dados do seu cartão. Processamento via provedor certificado.
						</p>
					</header>

					{/* Nome do titular */}
					<TextInput
						label="Nome do titular"
						id="holderName"
						placeholder="Como está no cartão"
						register={register}
						errors={errors}
						onKeyDown={libForm.EnterTab}
						className="col-span-1"
						icon={faUser}
						required
						autoComplete="cc-name"
						aria-required="true"
					/>

					{/* Número do cartão */}
					<div className="col-span-1">
						<div className="relative">
							<TextInput
								label="Número do cartão"
								id="number"
								placeholder="•••• •••• •••• ••••"
								register={(name, opts) =>
									register(name, {
										...opts,
										onChange: (e) => setCardNumber(formatCardNumberSpaces(e.target.value)),
									})
								}
								errors={errors}
								onKeyDown={libForm.EnterTab}
								className="w-full"
								icon={faCreditCard}
								required
								inputRef={numberInputRef}
								value={cardNumber}
								autoComplete="cc-number"
								aria-invalid={clientSideError ? "true" : "false"}
								maxLength={22}
							/>
							<div className="absolute right-2 top-8 h-6 w-16 flex items-center justify-end pointer-events-none">
								{brand?.img && <img src={brand.img} alt={brand.name} className="max-h-6 rounded" />}
							</div>
						</div>

						{clientSideError && (
							<p className="mt-1 text-sm text-red-600" role="alert">
								{clientSideError}
							</p>
						)}
					</div>

					{/* Expiração */}
					<MaskInput
						label="Data de expiração"
						name="expirationDate"
						placeholder="MM/AA"
						control={control}
						errors={errors}
						required
						onKeyDown={libForm.EnterTab}
						format="##/##"
						className="col-span-1"
						icon={faCalendarAlt}
						autoComplete="cc-exp"
					/>

					{/* CVV */}
					<TextInput
						label={"Código de segurança"}
						id="ccv"
						placeholder={cvvLen === 4 ? "4 dígitos" : "3 ou 4 dígitos"}
						register={register}
						errors={errors}
						onKeyDown={libForm.EnterTab}
						className="col-span-1"
						icon={faLock}
						required
						maxLength={4}
						autoComplete="cc-csc"
						aria-describedby="cvv-help"
					/>

					{/* Aviso de segurança (linha inteira) */}
					<div className="mt-2 flex items-center gap-2 text-xs text-gray-500 flex-wrap sm:col-span-2">
						<FontAwesomeIcon icon={faLock} className="h-3.5 w-3.5 flex-shrink-0" />
						<span className="break-words">
							Seus dados são criptografados. Recomendamos usar conexão segura e manter seu navegador
							atualizado.
						</span>
					</div>

					{/* Botão (linha inteira, alinhado à direita em ≥ sm) */}
					<div className="sm:col-span-2 flex items-center justify-end mt-1">
						<BtnSubmit
							text={isSubmitting ? "Processando..." : "Cadastrar cartão"}
							className="btn btn-primary w-full sm:w-64"
							isSubmitting={isSubmitting}
							disabled={isSubmitting}
							aria-disabled={isSubmitting}
						/>
					</div>
				</div>
			</section>
		</form>
	);
};

export default FormCreditCard;
